import { useCallback, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { api } from "../services/api";
import type { Notice } from "../services/api/types";

export function useScheduledNoticeActions(onSuccess?: () => void) {
  const { token, user } = useAuth();
  const { showMessage } = useSnackbar();
  const [actionNoticeId, setActionNoticeId] = useState<string | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<Notice | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Notice | null>(null);
  const [publishTarget, setPublishTarget] = useState<Notice | null>(null);
  const [savingReschedule, setSavingReschedule] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const handleCancelClick = useCallback((notice: Notice) => {
    setCancelTarget(notice);
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelTarget) return;
    const noticeId = cancelTarget.noticeId || cancelTarget.id;
    if (!noticeId || !user?.schoolId || !token) return;
    setCancelling(true);
    setActionNoticeId(noticeId);
    try {
      await api.notices.delete(noticeId, user.schoolId, token);
      setCancelTarget(null);
      onSuccess?.();
      showMessage({ message: "Scheduled notice cancelled.", severity: "success" });
    } catch (err) {
      showMessage({
        message:
          err instanceof Error && err.message
            ? err.message
            : "Couldn't cancel the scheduled notice. Please try again.",
        severity: "error",
      });
    } finally {
      setCancelling(false);
      setActionNoticeId(null);
    }
  }, [cancelTarget, onSuccess, showMessage, token, user?.schoolId]);

  const handlePublishNowClick = useCallback((notice: Notice) => {
    setPublishTarget(notice);
  }, []);

  const handlePublishNowConfirm = useCallback(async () => {
    if (!publishTarget) return;
    const noticeId = publishTarget.noticeId || publishTarget.id;
    if (!noticeId || !token) return;
    setPublishing(true);
    setActionNoticeId(noticeId);
    try {
      await api.notices.publishScheduledNow(noticeId, token);
      setPublishTarget(null);
      onSuccess?.();
      showMessage({ message: "Notice published.", severity: "success" });
    } catch (err) {
      showMessage({
        message:
          err instanceof Error && err.message
            ? err.message
            : "Couldn't publish the notice. Please try again.",
        severity: "error",
      });
      void api.ops.reportClientEvent(
        {
          kind: "publish_now_error",
          path: `POST /v1/notices/${noticeId}/publish-now`,
          message:
            err instanceof Error ? err.message.slice(0, 500) : "publish-now failed",
        },
        token
      );
    } finally {
      setPublishing(false);
      setActionNoticeId(null);
    }
  }, [onSuccess, publishTarget, showMessage, token]);

  const handleRescheduleSave = useCallback(
    async (notice: Notice, publishAt: string) => {
      const noticeId = notice.noticeId || notice.id;
      if (!noticeId || !token) return;
      setSavingReschedule(true);
      try {
        await api.notices.reschedulePublish(noticeId, publishAt, token);
        setRescheduleTarget(null);
        onSuccess?.();
        showMessage({ message: "Schedule updated.", severity: "success" });
      } catch (err) {
        showMessage({
          message:
            err instanceof Error && err.message
              ? err.message
              : "Couldn't update the schedule. Please try again.",
          severity: "error",
        });
        void api.ops.reportClientEvent(
          {
            kind: "schedule_mutation_error",
            path: `PATCH /v1/notices/${noticeId}/schedule`,
            message:
              err instanceof Error
                ? err.message.slice(0, 500)
                : "reschedule failed",
          },
          token
        );
      } finally {
        setSavingReschedule(false);
      }
    },
    [onSuccess, showMessage, token]
  );

  return {
    actionNoticeId,
    rescheduleTarget,
    cancelTarget,
    publishTarget,
    savingReschedule,
    cancelling,
    publishing,
    setRescheduleTarget,
    setCancelTarget,
    setPublishTarget,
    handleCancelClick,
    handleCancelConfirm,
    handlePublishNowClick,
    handlePublishNowConfirm,
    handleRescheduleSave,
  };
}
